// BoardReports.jsx — printable standardized report packs
import React, { useEffect, useState, useRef } from "react";
import {
  Box, Paper, Button, MenuItem, TextField, CircularProgress, Chip, Table, TableHead, TableRow, TableCell, TableBody, Alert,
} from "@mui/material";
import { Printer, FileText, Newspaper, Shield, Leaf, FileCheck2 } from "lucide-react";
import DashboardLayout from "../layouts/DashboardLayout";
import ScenarioToggle from "../components/ScenarioToggle";
import RiskPill from "../components/RiskPill";
import { reportsApi, entitiesApi } from "../api/extra";
import { fmtINR, fmtScore, fmtBps, fmtPct, fmtNum, fmtDateTime, HAZARD_LABELS } from "../utils/format";

const REPORTS = [
  { key: "risk_committee", label: "Risk Committee Pack", icon: Shield, fn: "riskCommittee", color: "#1E2A38" },
  { key: "esg_committee",  label: "ESG Committee Pack",  icon: Leaf,   fn: "esgCommittee",  color: "#2E5A35" },
  { key: "board",          label: "Board Climate Pack",  icon: Newspaper, fn: "boardPack",  color: "#8F5E14" },
  { key: "tcfd",           label: "TCFD Disclosure",     icon: FileText,  fn: "tcfd",       color: "#5D4037" },
  { key: "brsr",           label: "BRSR / RBI Disclosure", icon: FileCheck2, fn: "brsr",     color: "#A93226" },
];

export default function BoardReports() {
  const [reportKey, setReportKey] = useState("risk_committee");
  const [scenario, setScenario] = useState("current");
  const [entityCode, setEntityCode] = useState("IN-PARENT");
  const [entityList, setEntityList] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    entitiesApi.list().then(({ data }) => setEntityList(data.entities || []));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = REPORTS.find((x) => x.key === reportKey);
      const { data } = await reportsApi[r.fn](scenario, entityCode);
      setData(data.report);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reportKey, scenario, entityCode]);

  const doPrint = () => {
    window.print();
  };

  return (
    <DashboardLayout
      title="Board & Committee Reports"
      subtitle="STANDARDIZED · AUDIT-READY · PRINT-TO-PDF"
      headerExtra={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <ScenarioToggle value={scenario} onChange={setScenario} />
          <Button size="small" variant="contained" onClick={doPrint} startIcon={<Printer size={13} />}
            data-testid="print-report-btn"
            sx={{ background: "#1E2A38", "&:hover": { background: "#0F1721" } }}>
            Print / Save PDF
          </Button>
        </Box>
      }
    >
      {/* Print-only CSS */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #FFFFFF !important; }
          .print-area {
            background: #FFFFFF;
            box-shadow: none;
            border: none;
            padding: 0 !important;
            margin: 0 !important;
          }
          .print-page { page-break-after: always; }
          .print-page:last-child { page-break-after: auto; }
        }
        .print-area {
          background: #FFFFFF;
        }
      `}</style>

      {/* Toolbar: choose report + entity */}
      <Paper sx={{ p: 1.6, mb: 2 }} className="no-print" data-testid="report-toolbar">
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
            {REPORTS.map((r) => {
              const Icon = r.icon;
              const active = reportKey === r.key;
              return (
                <Button key={r.key} size="small"
                  variant={active ? "contained" : "outlined"}
                  onClick={() => setReportKey(r.key)}
                  startIcon={<Icon size={13} />}
                  data-testid={`report-pick-${r.key}`}
                  sx={{
                    background: active ? r.color : "transparent",
                    color: active ? "#FFFFFF" : "#111827",
                    borderColor: active ? r.color : "#CBD5E1",
                    "&:hover": { background: active ? r.color : "#F0EFEA", borderColor: r.color },
                  }}>
                  {r.label}
                </Button>
              );
            })}
          </Box>
          <Box sx={{ flex: 1 }} />
          <TextField size="small" select label="Entity" value={entityCode}
            onChange={(e) => setEntityCode(e.target.value)} sx={{ minWidth: 220 }}
            SelectProps={{ inputProps: { "data-testid": "report-entity-select" } }}>
            {entityList.map((e) => (
              <MenuItem key={e.code} value={e.code}>
                {e.name} ({e.jurisdiction})
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>

      {loading || !data ? (
        <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={26} /></Box>
      ) : (
        <Paper sx={{ p: { xs: 2, md: 5 }, background: "#FFFFFF" }} className="print-area" ref={printRef} data-testid="report-canvas">
          <ReportCover data={data} reportKey={reportKey} />
          <Section1Snapshot data={data} />
          <Section2Vulnerability data={data} />
          <Section3TopExposures data={data} />
          <Section4Operations data={data} />
          <Section5Recommendations data={data} />
          {reportKey === "tcfd" && <SectionTCFD data={data} />}
          {reportKey === "brsr" && <SectionBRSR data={data} />}
          <SectionSignoff data={data} reportKey={reportKey} />
        </Paper>
      )}
    </DashboardLayout>
  );
}

// -------- Report Sections --------

function ReportCover({ data, reportKey }) {
  const R = REPORTS.find((x) => x.key === reportKey);
  const title = R?.label || "Report";
  const e = data.entity || {};
  return (
    <Box className="print-page" sx={{ mb: 5 }} data-testid="report-cover">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: `2px solid ${R?.color || "#1E2A38"}`, pb: 2, mb: 3 }}>
        <Box>
          <Box className="cb-mono" sx={{ fontSize: 11, color: "#6B7280", letterSpacing: "0.15em", textTransform: "uppercase" }}>
            ClimateLedger · Banking Physical Risk
          </Box>
          <Box className="cb-display" sx={{ fontSize: 36, fontWeight: 600, color: "#111827", mt: 0.6, lineHeight: 1.1 }}>
            {title}
          </Box>
          <Box sx={{ fontSize: 14, color: "#4B5563", mt: 1.4, maxWidth: 600, lineHeight: 1.5 }}>
            Physical climate-risk assessment compiled from the integrated geospatial, collateral, operations
            and origination data inside ClimateLedger.
          </Box>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Box className="cb-kpi-label">Reporting Entity</Box>
          <Box sx={{ fontWeight: 600, fontSize: 14 }}>{e.name || "—"}</Box>
          <Box className="cb-mono" sx={{ fontSize: 11.5, color: "#6B7280" }}>{e.code} · {e.jurisdiction} · {e.regulator}</Box>
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
        <Meta label="Scenario" value={data.scenario.toUpperCase()} />
        <Meta label="Generated" value={fmtDateTime(data.generated_at)} />
        <Meta label="Currency" value={e.reporting_currency || "INR"} />
        <Meta label="Open Action Plans" value={fmtNum(data.open_action_plans_count)} />
      </Box>
    </Box>
  );
}

function Section1Snapshot({ data }) {
  const s = data.portfolio_summary || {};
  return (
    <Box className="print-page" sx={{ mb: 5 }} data-testid="report-section-snapshot">
      <SectionTitle n="1" title="Executive Snapshot" />
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mb: 2 }}>
        <Big label="Portfolio collateral value" value={fmtINR(data.total_collateral_value_inr)} />
        <Big label="Net financial exposure" value={fmtINR(s.total_financial_exposure_inr)} accent="#A93226" />
        <Big label="Avg climate PD uplift" value={fmtBps(s.avg_pd_uplift_bps)} accent="#8F5E14" />
        <Big label="High-risk share" value={fmtPct(s.high_risk_pct)} accent="#A93226" />
      </Box>
      <Box sx={{ background: "#FBFBFA", border: "1px solid #E2E8F0", borderRadius: 1, p: 2 }}>
        <Box sx={{ fontSize: 12.5, color: "#4B5563", lineHeight: 1.65 }}>
          <b>Commentary.</b> Against the <b>{data.scenario}</b> scenario, the portfolio shows a weighted-average
          climate risk score of <b>{fmtScore(s.avg_score)}</b> ({s.risk_level || "—"}), with{" "}
          <b>{fmtPct(s.high_risk_pct)}</b> of collateral classified High or Critical. Total computed financial
          exposure stands at <b>{fmtINR(s.total_financial_exposure_inr)}</b> on{" "}
          <b>{fmtNum(s.collateral_count)}</b> pinpointed assets. Average climate-driven PD uplift across the
          book is <b>{fmtBps(s.avg_pd_uplift_bps)}</b>, which the Risk function recommends embedding into
          risk-based pricing per the COV-003 strategy.
        </Box>
      </Box>
    </Box>
  );
}

function Section2Vulnerability({ data }) {
  const v = data.vulnerability || {};
  return (
    <Box className="print-page" sx={{ mb: 5 }}>
      <SectionTitle n="2" title="Vulnerability Categorization" />
      <Box sx={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 3, alignItems: "start" }}>
        <Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tier</TableCell>
                <TableCell align="right">Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow><TableCell sx={{ color: "#4A1A1A", fontWeight: 600 }}>T5 — Severe</TableCell><TableCell align="right" className="cb-mono">{v.T5_Severe || 0}</TableCell></TableRow>
              <TableRow><TableCell sx={{ color: "#A93226", fontWeight: 600 }}>T4 — High</TableCell><TableCell align="right" className="cb-mono">{v.T4_High || 0}</TableCell></TableRow>
              <TableRow><TableCell sx={{ color: "#8F5E14", fontWeight: 600 }}>T3 — Moderate</TableCell><TableCell align="right" className="cb-mono">{v.T3_Moderate || 0}</TableCell></TableRow>
              <TableRow><TableCell sx={{ color: "#A3C2A9", fontWeight: 600 }}>T2 — Low</TableCell><TableCell align="right" className="cb-mono">{v.T2_Low || 0}</TableCell></TableRow>
              <TableRow><TableCell sx={{ color: "#2E5A35", fontWeight: 600 }}>T1 — Minimal</TableCell><TableCell align="right" className="cb-mono">{v.T1_Minimal || 0}</TableCell></TableRow>
            </TableBody>
          </Table>
        </Box>
        <Box>
          <Box className="cb-kpi-label">Vulnerable share (T3+T4+T5)</Box>
          <Box className="cb-display" sx={{ fontSize: 56, fontWeight: 600, color: "#A93226", lineHeight: 1.05 }}>
            {fmtPct(v.pct_value_vulnerable)}
          </Box>
          <Box className="cb-mono" sx={{ fontSize: 12, color: "#6B7280", mt: 0.4 }}>by value</Box>
          <Box sx={{ mt: 1.6 }}>
            <Box className="cb-kpi-label">Vulnerable value</Box>
            <Box className="cb-mono" sx={{ fontSize: 16, fontWeight: 600 }}>{fmtINR(v.value_T5_T4_T3_inr)}</Box>
          </Box>
          <Box sx={{ mt: 1.2 }}>
            <Box className="cb-kpi-label">Vulnerable net exposure</Box>
            <Box className="cb-mono" sx={{ fontSize: 16, fontWeight: 600, color: "#8F5E14" }}>{fmtINR(v.exposure_T5_T4_T3_inr)}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function Section3TopExposures({ data }) {
  const rows = (data.top_exposures || []).slice(0, 8);
  return (
    <Box className="print-page" sx={{ mb: 5 }}>
      <SectionTitle n="3" title="Top Climate-Vulnerable Exposures" />
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Collateral / Borrower</TableCell>
            <TableCell>City · State</TableCell>
            <TableCell>Top hazard</TableCell>
            <TableCell align="right">Score</TableCell>
            <TableCell align="right">Risk</TableCell>
            <TableCell align="right">Net exposure</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.collateral_id}>
              <TableCell>
                <Box sx={{ fontWeight: 600, fontSize: 12.5 }}>{r.collateral_name}</Box>
                <Box className="cb-mono" sx={{ fontSize: 10.5, color: "#6B7280" }}>{r.borrower_cif_no} · {r.borrower_name}</Box>
              </TableCell>
              <TableCell sx={{ fontSize: 12 }}>{r.city || ""} · {r.state || ""}</TableCell>
              <TableCell><span className="hazard-chip">{HAZARD_LABELS[r.top_hazard] || r.top_hazard || "—"}</span></TableCell>
              <TableCell align="right" className="cb-mono" sx={{ fontWeight: 600 }}>{fmtScore(r.overall_score)}</TableCell>
              <TableCell align="right"><RiskPill level={r.risk_level} /></TableCell>
              <TableCell align="right" className="cb-mono">{fmtINR(r.financial_exposure_inr)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function Section4Operations({ data }) {
  const s = data.operations_summary || {};
  const fac = data.facility_scored || [];
  const high = fac.filter((f) => f.risk_level === "High" || f.risk_level === "Critical").slice(0, 5);
  return (
    <Box className="print-page" sx={{ mb: 5 }}>
      <SectionTitle n="4" title="Bank's Own Operations Risk" />
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mb: 2 }}>
        <Big label="Facilities scored" value={fmtNum(s.collateral_count)} />
        <Big label="Avg score" value={fmtScore(s.avg_score)} />
        <Big label="High-risk share" value={fmtPct(s.high_risk_pct)} accent="#A93226" />
        <Big label="Replacement value at risk" value={fmtINR(s.total_financial_exposure_inr)} accent="#8F5E14" />
      </Box>
      {high.length > 0 ? (
        <>
          <Box className="cb-kpi-label" sx={{ mb: 1 }}>Facilities flagged High or Critical</Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Facility</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Top hazard</TableCell>
                <TableCell align="right">Score</TableCell>
                <TableCell align="right">Risk</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {high.map((f) => (
                <TableRow key={f.facility_id}>
                  <TableCell sx={{ fontWeight: 600 }}>{f.facility_name}</TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>{f.facility_type?.replace("_", " ")}</TableCell>
                  <TableCell><span className="hazard-chip">{HAZARD_LABELS[f.top_hazard] || f.top_hazard || "—"}</span></TableCell>
                  <TableCell align="right" className="cb-mono" sx={{ fontWeight: 600 }}>{fmtScore(f.overall_score)}</TableCell>
                  <TableCell align="right"><RiskPill level={f.risk_level} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      ) : (
        <Alert severity="success" icon={false}>No facility currently classified High/Critical at this scenario.</Alert>
      )}
    </Box>
  );
}

function Section5Recommendations({ data }) {
  const pr = data.portfolio_recommendations || [];
  const op = data.operations_recommendations || [];
  return (
    <Box className="print-page" sx={{ mb: 5 }}>
      <SectionTitle n="5" title="Recommended Strategies & Action Plan" />

      <Box className="cb-kpi-label" sx={{ mt: 1, mb: 0.6 }}>Portfolio level</Box>
      <RecoList recos={pr} />

      <Box className="cb-kpi-label" sx={{ mt: 2.4, mb: 0.6 }}>Operations level</Box>
      <RecoList recos={op} />
    </Box>
  );
}

function RecoList({ recos }) {
  const PRIO = { P0: "#A93226", P1: "#8F5E14", P2: "#2E5A35" };
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Strategy</TableCell>
          <TableCell>Category</TableCell>
          <TableCell>Owner</TableCell>
          <TableCell>Priority</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {recos.length === 0 && <TableRow><TableCell colSpan={4} sx={{ py: 2, textAlign: "center", color: "#94A3B8" }}>No specific recommendations.</TableCell></TableRow>}
        {recos.map((r) => (
          <TableRow key={r.strategy_id}>
            <TableCell>
              <Box sx={{ fontWeight: 600, fontSize: 12.5 }}>{r.title}</Box>
              <Box className="cb-mono" sx={{ fontSize: 10, color: "#6B7280" }}>{r.strategy_id}</Box>
              {r.rationale && <Box sx={{ fontSize: 11, color: "#4B5563", mt: 0.4, lineHeight: 1.45 }}>{r.rationale}</Box>}
            </TableCell>
            <TableCell sx={{ fontSize: 11.5 }}>{r.category}</TableCell>
            <TableCell sx={{ fontSize: 11.5 }}>{r.owner_dept}</TableCell>
            <TableCell>
              <Chip size="small" label={r.priority}
                sx={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10, fontWeight: 700, background: PRIO[r.priority] + "22", color: PRIO[r.priority] }} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function SectionTCFD({ data }) {
  const s = data.tcfd_sections || {};
  return (
    <Box className="print-page" sx={{ mb: 5 }} data-testid="report-tcfd-sections">
      <SectionTitle n="6" title="TCFD Disclosure Alignment" />
      <Box sx={{ display: "grid", gap: 1.6 }}>
        {["governance", "strategy", "risk_management", "metrics_targets"].map((k) => (
          <Paper key={k} sx={{ p: 2, background: "#FBFBFA" }}>
            <Box className="cb-kpi-label">{k.replace("_", " ")}</Box>
            <Box sx={{ fontSize: 12.5, color: "#4B5563", mt: 0.6, lineHeight: 1.55 }}>{s[k] || "—"}</Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

function SectionBRSR({ data }) {
  const s = data.brsr_sections || {};
  return (
    <Box className="print-page" sx={{ mb: 5 }} data-testid="report-brsr-sections">
      <SectionTitle n="6" title="BRSR / RBI Disclosure Sections" />
      <Box sx={{ display: "grid", gap: 1.6 }}>
        <Paper sx={{ p: 2, background: "#FBFBFA" }}>
          <Box className="cb-kpi-label">Principle 6 — Environmental Responsibility</Box>
          <Box sx={{ fontSize: 12.5, color: "#4B5563", mt: 0.6, lineHeight: 1.55 }}>{s.principle_6}</Box>
        </Paper>
        <Paper sx={{ p: 2, background: "#FBFBFA" }}>
          <Box className="cb-kpi-label">Principle 9 — Customer Welfare</Box>
          <Box sx={{ fontSize: 12.5, color: "#4B5563", mt: 0.6, lineHeight: 1.55 }}>{s.principle_9}</Box>
        </Paper>
      </Box>
    </Box>
  );
}

function SectionSignoff({ data, reportKey }) {
  return (
    <Box sx={{ mt: 4, pt: 3, borderTop: "1px dashed #CBD5E1" }} data-testid="report-signoff">
      <Box className="cb-kpi-label" sx={{ mb: 2 }}>Sign-off block</Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
        {[
          { role: "Prepared by", dept: "Risk · Climate Risk Cell" },
          { role: "Reviewed by", dept: "Chief Risk Officer" },
          { role: "Approved by", dept: reportKey === "esg_committee" ? "ESG Committee Chair" : reportKey === "board" ? "Board Chair" : "Risk Committee Chair" },
        ].map((s) => (
          <Box key={s.role} sx={{ borderTop: "1px solid #111827", pt: 1 }}>
            <Box sx={{ fontSize: 12, fontWeight: 600 }}>{s.role}</Box>
            <Box sx={{ fontSize: 11, color: "#6B7280" }}>{s.dept}</Box>
            <Box sx={{ fontSize: 10.5, color: "#94A3B8", mt: 2.4 }}>Signature / Date</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function SectionTitle({ n, title }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.4, mb: 2 }}>
      <Box className="cb-display" sx={{ fontSize: 22, fontWeight: 600, color: "#A93226", minWidth: 30 }}>{n}</Box>
      <Box className="cb-display" sx={{ fontSize: 22, fontWeight: 600, color: "#111827" }}>{title}</Box>
    </Box>
  );
}

function Big({ label, value, accent = "#1E2A38" }) {
  return (
    <Box sx={{ p: 1.6, border: `1px solid #E2E8F0`, borderLeft: `3px solid ${accent}`, borderRadius: 1 }}>
      <Box className="cb-kpi-label">{label}</Box>
      <Box className="cb-mono" sx={{ fontSize: 19, fontWeight: 600, mt: 0.4 }}>{value}</Box>
    </Box>
  );
}

function Meta({ label, value }) {
  return (
    <Box>
      <Box className="cb-kpi-label">{label}</Box>
      <Box className="cb-mono" sx={{ fontSize: 13, fontWeight: 600, mt: 0.3 }}>{value}</Box>
    </Box>
  );
}
